import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone, ViewChild, TemplateRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription, firstValueFrom } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { environment } from '../../../environments/environment';
import { csvFileDateSuffix, downloadCsv } from '../../utils/csv.util';
import { formatMemberExpiry } from '../../utils/membership.util';
import { getPhoneNumberFrom } from '../../interfaces/user-information-summary.interface';
import { SafePipe } from '../../pipes/safe.pipe';

// Form Request New Member Interface (API Response)
export interface FormRequestNewMember {
  id: string;
  userId: string;
  educationLevel: string;
  workExperienceYears: number;
  membershipType: string;
  feeAmount: string;
  guarantor1: string;
  guarantor2: string;
  documents: Array<{
    name: string;
    file: string;
  }>;
  status: string;
  paymentStatus: string;
  paymentSlip: string | null;
  remarks: string | null;
  amount: string | null;
  originalSlipStatus: boolean;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    cid: string;
    firstName?: string;
    lastName?: string;
    isActive: boolean;
    userInformation?: {
      firstNameThai?: string;
      lastNameThai?: string;
      firstNameEnglish?: string;
      lastNameEnglish?: string;
      vatId?: string;
      vatType?: string;
      [key: string]: any;
    };
  };
}

// Form Renew Member Interface (API Response)
export interface FormRenewMember {
  id: string;
  userId: string;
  membershipType: string;
  feeAmount: string;
  documents: Array<{
    name: string;
    file: string;
  }>;
  status: string;
  paymentStatus: string;
  paymentSlip: string | null;
  remarks: string | null;
  amount: string | null;
  originalSlipStatus: boolean;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    cid: string;
    firstName?: string;
    lastName?: string;
    isActive: boolean;
    userInformation?: {
      firstNameThai?: string;
      lastNameThai?: string;
      firstNameEnglish?: string;
      lastNameEnglish?: string;
      vatId?: string;
      vatType?: string;
      [key: string]: any;
    };
  };
}

// Form Regis Appraiser Interface (API Response)
export interface FormRegisAppraiser {
  id: string;
  userId: string;
  appraiserLevel: string;
  status: string;
  paymentStatus: string;
  paymentSlip: string | null;
  remarks: string | null;
  amount: number | null;
  originalSlipStatus: boolean;
  codeReceipt: string | null;
  transferDateTime: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    cid: string;
    firstName?: string;
    lastName?: string;
    isActive: boolean;
    userInformation?: {
      firstNameThai?: string;
      lastNameThai?: string;
      firstNameEnglish?: string;
      lastNameEnglish?: string;
      firstName?: string;
      lastName?: string;
      [key: string]: any;
    };
  };
}

// Form Renew Appraiser Interface (API Response)
export interface FormRenewAppraiser {
  id: string;
  userId: string;
  appraiserLevel: string;
  status: string;
  paymentStatus: string;
  paymentSlip: string | null;
  remarks: string | null;
  amount: number | null;
  originalSlipStatus: boolean;
  codeReceipt: string | null;
  transferDateTime: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    cid: string;
    firstName?: string;
    lastName?: string;
    isActive: boolean;
    userInformation?: {
      firstNameThai?: string;
      lastNameThai?: string;
      firstNameEnglish?: string;
      lastNameEnglish?: string;
      firstName?: string;
      lastName?: string;
      [key: string]: any;
    };
  };
}

// CPD Hours Training Interface (API Response)
export interface CpdHoursTraining {
  id: string;
  userId: string;
  courseId: string;
  hours: number;
  cpdPaymentStatusId: number;
  paymentSlip: string | null;
  remarks: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    cid: string;
    firstName?: string;
    lastName?: string;
    isActive: boolean;
    userInformation?: {
      firstNameThai?: string;
      lastNameThai?: string;
      firstNameEnglish?: string;
      lastNameEnglish?: string;
      vatId?: string;
      vatType?: string;
      [key: string]: any;
    };
  };
  course: {
    id: string;
    title: string;
    description: string;
    instructor: string;
    duration: string;
    category: string;
    level: string;
    price: string;
    isActive: boolean;
  };
  cpdPaymentStatus: {
    id: number;
    name: string;
  };
}

// Exam List Interface (API Response)
export interface ExamList {
  id: string;
  hoursStudied: string;
  studyDate: string;
  year: number;
  sessionTitle: string;
  notes: string;
  isVerified: boolean;
  enrolledAt: string;
  completedAt: string | null;
  progress: string;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  userId: string;
  examId: string;
  examListStatusId: number;
  examListPaymentStatusId: number;
  paymentSlip: string | null;
  attachFile: string | null;
  amount: string;
  originalSlipStatus: boolean;
  typeReceipt: string;
  codeReceipt: string;
  originalReceipt: boolean;
  user: {
    id: string;
    email: string;
    password: string;
    cid: string;
    isActive: boolean;
    forcePasswordChange: boolean;
    emailForRegis: boolean;
    emailForgetPassword: boolean;
    createdAt: string;
    updatedAt: string;
    userInformation?: {
      id: string;
      titleName: string;
      firstNameThai: string;
      lastNameThai: string;
      firstNameEnglish: string;
      lastNameEnglish: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      address: string;
      subdistrict: string;
      district: string;
      province: string;
      postalCode: string;
      gender: string;
      profilePicture: string;
      organization: string;
      vatId: string;
      vatType: string;
      vatExpired: string | null;
      valuerId: string;
      valuerType: string;
      valuerExpired: string | null;
      vatCpd: string;
      tvaCpd: string;
      otherCpd: string;
      totalCpd: string;
      emailForApproved: boolean;
      createdAt: string;
      updatedAt: string;
      userStatusId: number;
      userStatusNote: string | null;
      companyId: string;
      [key: string]: any;
    };
  };
  exam: {
    id: string;
    examTitle: string;
    valuerRank: string;
    registrationDeadline: string;
    examDate: string;
    examTime: string;
    examLocation: string;
    examFee: string;
    remarks: string;
    posterFile: string | null;
    certificateFile: string | null;
    createdAt: string;
    updatedAt: string;
    examStatusId: number;
  };
  examListStatus: {
    id: number;
    name: string;
  };
  examListPaymentStatus: {
    id: number;
    name: string;
  };
}

// Receipt Log Interface (API Response)
export interface ReceiptLog {
  id: string;
  examListId: string | null;
  formRenewMemberId: string | null;
  formRenewAppraiserId: string | null;
  formRegisAppraiserId: string | null;
  formRequestNewMemberId: string | null;
  type: string;
  transferDateTime: string;
  status: string;
  typeReceipt: string;
  usedFor: string; // receipt type the user chose: "company" | "individual"
  codeReceipt: string;
  amount: string;
  cancelReason?: string | null;
  createdDate: string;
  updatedDate: string;
  examList?: {
    id: string;
    sessionTitle: string;
    studyDate: string;
    year: number;
    amount: string;
    paymentSlip: string | null;
    attachFile: string | null;
    transferDateTime: string;
    user: {
      id: string;
      email: string;
      cid: string;
      userInformation?: {
        firstName: string;
        lastName: string;
        firstNameThai: string;
        lastNameThai: string;
        firstNameEnglish: string;
        lastNameEnglish: string;
        [key: string]: any;
      };
    };
  };
}

export interface ReceiptLogListResponse {
  data: ReceiptLog[];
  total: number;
  summary?: { created: number; canceled: number };
}

// Payment Record Interface (API Response)
export interface PaymentRecord {
  id: string;
  userId: string;
  courseId?: string;
  examId?: string;
  amount: number;
  paymentSlip: string | null;
  paymentDate: string;
  verifiedDate: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
  paymentStatusId: number;
  user: {
    id: string;
    email: string;
    cid: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    companyId: string | null;
  };
  course?: {
    id: string;
    title: string;
    description: string;
    instructor: string;
    duration: string;
    category: string;
    level: string;
    price: string;
    isActive: boolean;
  };
  exam?: {
    id: string;
    title: string;
    description: string;
    instructor: string;
    duration: string;
    category: string;
    level: string;
    price: string;
    isActive: boolean;
  };
  paymentStatus: {
    id: number;
    name: string;
  };
  receiptLogId?: string;
  codeReceipt?: string;
  type?: string;
}

// Filters Interface
export interface PaymentFilters {
  searchTerm: string;
  email: string;
  codeReceipt: string;
  itemTitle: string;
  receiptType: string; // '' | 'created' | 'canceled'
  amountMin: string;
  amountMax: string;
  dateFrom: string;
  dateTo: string;
}

@Component({
  selector: 'app-account-worklist',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbPaginationModule,
    NgbTooltipModule,
    NgbAlertModule,
    NgbDropdownModule,
    StaffHeaderComponent,
    StaffNavComponent,
    SafePipe
  ],
  templateUrl: './account-worklist.component.html',
  styleUrl: './account-worklist.component.scss'
})
export class AccountWorklistComponent implements OnInit, OnDestroy {
  @ViewChild('receiptLogModal') receiptLogModal!: TemplateRef<any>;

  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  private userSubscription: Subscription | undefined;

  // Receipt log dialog state
  receiptLogs: ReceiptLog[] = [];
  receiptLogSummary: { created: number; canceled: number } | null = null;
  receiptLogLoading = false;
  // Tracks which form entity and tab are being shown in the dialog (for form types)
  selectedFormEntityId: string | null = null;
  selectedFormTabType: string | null = null;

  records: PaymentRecord[] = [];
  filteredRecords: PaymentRecord[] = [];
  memberCardRecords: FormRequestNewMember[] = [];
  filteredMemberCardRecords: FormRequestNewMember[] = [];
  renewMemberRecords: FormRenewMember[] = [];
  filteredRenewMemberRecords: FormRenewMember[] = [];
  regisAppraiserRecords: FormRegisAppraiser[] = [];
  filteredRegisAppraiserRecords: FormRegisAppraiser[] = [];
  renewAppraiserRecords: FormRenewAppraiser[] = [];
  filteredRenewAppraiserRecords: FormRenewAppraiser[] = [];
  trainingRecords: CpdHoursTraining[] = [];
  filteredTrainingRecords: CpdHoursTraining[] = [];
  examRecords: ReceiptLog[] = [];
  filteredExamRecords: ReceiptLog[] = [];
  isLoading = false;
  isExporting = false;
  error: string | null = null;

  // Tab management
  activeTab: 'member-card' | 'renew-member' | 'register-appraiser' | 'renew-appraiser' | 'training' | 'exam' = 'member-card';
  tabs = [
    { id: 'member-card' as const, label: 'ขอทำบัตรสมาชิก', badgeClass: 'bg-primary' },
    { id: 'renew-member' as const, label: 'ขอต่ออายุสมาชิก', badgeClass: 'bg-success' },
    { id: 'register-appraiser' as const, label: 'ขอขึ้นทะเบียน', badgeClass: 'bg-info' },
    { id: 'renew-appraiser' as const, label: 'ขอต่ออายุบัตร', badgeClass: 'bg-warning' },
    { id: 'training' as const, label: 'การอบรม', badgeClass: 'bg-primary' },
    { id: 'exam' as const, label: 'การสมัครสอบ', badgeClass: 'bg-info' }
  ];
  
  // Tab counts - store counts for each tab separately
  tabCounts: { [key: string]: number } = {
    'member-card': 0,
    'renew-member': 0,
    'register-appraiser': 0,
    'renew-appraiser': 0,
    training: 0,
    exam: 0
  };

  // Filter properties
  filters: PaymentFilters = {
    searchTerm: '',
    email: '',
    codeReceipt: '',
    itemTitle: '',
    receiptType: '',
    amountMin: '',
    amountMax: '',
    dateFrom: '',
    dateTo: ''
  };

  // Pagination
  page = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50, 100];

  /**
   * จำนวนรายการต่อหนึ่งครั้งที่ขอจาก API
   * หน้านี้กรอง/แบ่งหน้าฝั่ง client จึงต้องดึงมาให้ครบทุกรายการ โดยวนขอทีละหน้า
   */
  private readonly RECEIPT_LOG_PAGE_SIZE = 500;

  /** เพดานจำนวนหน้าที่ยอมวนขอ กันลูปไม่รู้จบเมื่อ API ตอบผิดรูปแบบ */
  private readonly RECEIPT_LOG_MAX_PAGES = 100;
  collectionSize = 0;

  // Sorting
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // หลักฐานการชำระเงิน (ดูแบบย่อ-ขยายได้)
  showSlipPreview = false;
  slipPreviewUrl: string | null = null;
  slipPreviewIsPdf = false;
  slipZoom = 1;
  slipOffsetX = 0;
  slipOffsetY = 0;
  private slipDragging = false;
  private slipDragStartX = 0;
  private slipDragStartY = 0;
  private readonly SLIP_ZOOM_MIN = 0.25;
  private readonly SLIP_ZOOM_MAX = 5;
  private readonly SLIP_ZOOM_STEP = 0.25;

  private subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modalService: NgbModal,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    console.log('🔄 AccountWorklistComponent ngOnInit');

    // Subscribe to auth user
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      console.log('👤 Current user updated:', user);
      this.currentUser = user;
      this.cdr.detectChanges();
    });

    // Load data immediately inside NgZone
    this.ngZone.run(() => {
      console.log('📥 Loading payment records data immediately on init');
      setTimeout(() => {
        this.loadPaymentRecords();
      }, 0);
    });
  }

  ngOnDestroy(): void {
    console.log('🗑️ AccountWorklistComponent ngOnDestroy');
    this.modalService.dismissAll();
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this.subscription.unsubscribe();
  }

  onLogout(): void {
    this.authService.logout();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();

    switch (route) {
      case 'users':
        this.router.navigate(['/staff/user-management']);
        break;
      case 'courses':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'reports':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'settings':
        this.router.navigate(['/staff/dashboard']);
        break;
      default:
        this.router.navigate(['/staff/dashboard']);
        break;
    }
  }

  /**
   * Load all payment records from API
   */
  loadPaymentRecords(): void {
    console.log('🚀 Loading payment records from API...');
    console.log('📊 Current page:', this.page, 'Page size:', this.pageSize);
    console.log('📑 Active tab:', this.activeTab);

    this.isLoading = true;
    this.error = null;
    this.cdr.detectChanges();

    // Load member-card data from form-request-new-member API
    if (this.activeTab === 'member-card') {
      this.loadMemberCardRecords();
    } else if (this.activeTab === 'renew-member') {
      this.loadRenewMemberRecords();
    } else if (this.activeTab === 'register-appraiser') {
      this.loadRegisAppraiserRecords();
    } else if (this.activeTab === 'renew-appraiser') {
      this.loadRenewAppraiserRecords();
    } else if (this.activeTab === 'training') {
      this.loadTrainingRecords();
    } else if (this.activeTab === 'exam') {
      this.loadExamRecords();
    } else {
      // Load other payment records
      const apiUrl = `${environment.apiUrl}/payments`;
      console.log('📡 Fetching from:', apiUrl);

      const sub = this.http.get<PaymentRecord[]>(apiUrl).subscribe({
        next: (response) => {
          this.ngZone.run(() => {
            console.log('✅ API Response:', response);
            console.log('📦 Data count:', response?.length || 0);
            
            this.records = response || [];
            this.updateTabCounts();
            this.applyTabFilter();
            this.isLoading = false;
            
            console.log('✨ Filtered records count:', this.filteredRecords.length);
            console.log('✨ Collection size:', this.collectionSize);
            
            this.cdr.detectChanges();
          });
        },
        error: (error: HttpErrorResponse) => {
          this.ngZone.run(() => {
            console.error('❌ API Error:', error);
            console.error('❌ Error status:', error.status);
            console.error('❌ Error message:', error.message);
            console.error('❌ Error details:', error.error);
            
            // Handle 304 Not Modified as success with cached data
            if (error.status === 304) {
              console.log('✅ 304 Not Modified - Using cached data');
              this.isLoading = false;
              this.cdr.detectChanges();
              return;
            }
            
            this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล: ${error.message}`;
            this.records = [];
            this.filteredRecords = [];
            this.collectionSize = 0;
            this.isLoading = false;
            this.cdr.detectChanges();
          });
        }
      });
      
      this.subscription.add(sub);
    }
  }

  /**
   * Map receipt_logs into the form-shaped record each form tab already renders.
   * Spreads the source form relation (user, membershipType, appraiserLevel, …)
   * and overrides amount/status/date with the frozen receipt values. Skips rows
   * whose source was deleted (FK SET NULL).
   */
  private mapFormReceiptLogs(logs: any[], sourceKey: string): any[] {
    return (logs || [])
      .map((log: any) => {
        const src = log[sourceKey];
        if (!src) return null;
        const amountNum = parseFloat(log.amount) || 0;
        return {
          ...src,
          amount: amountNum,
          feeAmount: amountNum,
          paymentStatus: log.status,
          transferDateTime: log.transferDateTime,
          codeReceipt: log.codeReceipt,
          usedFor: log.usedFor,
          type: log.type,
          receiptLogId: log.id,
          cancelReason: log.cancelReason ?? null, // เหตุผลการยกเลิกใบเสร็จ (ใช้ใน CSV)
          createdDate: log.createdDate, // receipt-log timestamp (ISO) for date filtering
        };
      })
      .filter((r: any) => r !== null);
  }

  /**
   * Map receipt_logs into the cpd/training-shaped record the training tab renders
   * (status via cpdPaymentStatus.name, amount via course.price).
   */
  private mapCpdReceiptLogs(logs: any[]): any[] {
    return (logs || [])
      .map((log: any) => {
        const c = log.cpdHours;
        if (!c) return null;
        const amountNum = parseFloat(log.amount) || 0;
        return {
          ...c,
          amount: amountNum,
          course: { ...(c.course || {}), price: amountNum },
          cpdPaymentStatus: { name: log.status },
          paymentStatus: log.status,
          transferDateTime: log.transferDateTime,
          codeReceipt: log.codeReceipt,
          usedFor: log.usedFor,
          type: log.type,
          receiptLogId: log.id,
          cancelReason: log.cancelReason ?? null, // เหตุผลการยกเลิกใบเสร็จ (ใช้ใน CSV)
          createdDate: log.createdDate, // receipt-log timestamp (ISO) for date filtering
        };
      })
      .filter((r: any) => r !== null);
  }

  /**
   * Load renew member records from receipt-log API (typeReceipt=formRenewMember)
   */
  // ── โหลดใบเสร็จของแต่ละแท็บ ──

  /**
   * ดึง receipt-log ของประเภทที่ระบุมาให้ครบทุกรายการ
   *
   * API แบ่งหน้า เดิมโค้ดนี้ขอมาหน้าเดียว limit 1000 ถ้ามีมากกว่านั้นข้อมูลจะขาด
   * ไปเงียบ ๆ ทั้งบนตารางและในไฟล์ CSV จึงต้องวนขอจนครบตาม total ที่ API บอก
   */
  private async fetchAllReceiptLogs(typeReceipt: string): Promise<any[]> {
    const collected: any[] = [];
    let page = 1;
    let total = 0;

    do {
      const apiUrl = `${environment.apiUrl}/receipt-log?page=${page}` +
        `&limit=${this.RECEIPT_LOG_PAGE_SIZE}&typeReceipt=${typeReceipt}`;
      const body: any = await firstValueFrom(this.http.get<any>(apiUrl));

      const rows = this.extractReceiptLogRows(body);
      collected.push(...rows);
      total = typeof body?.total === 'number' ? body.total : collected.length;

      // API ไม่ส่งข้อมูลเพิ่มแล้ว หยุดทันทีกันลูปไม่รู้จบ
      if (rows.length === 0) break;
      page += 1;
    } while (collected.length < total && page <= this.RECEIPT_LOG_MAX_PAGES);

    if (collected.length < total) {
      console.warn(
        `⚠️ โหลด ${typeReceipt} ได้ ${collected.length} จาก ${total} รายการ ` +
        `(ชนเพดาน ${this.RECEIPT_LOG_MAX_PAGES} หน้า)`
      );
    }

    return collected;
  }

  /** รองรับทั้ง response ที่เป็น array ตรง ๆ และที่ห่อด้วย data / results */
  private extractReceiptLogRows(body: any): any[] {
    if (Array.isArray(body)) return body;
    if (Array.isArray(body?.data)) return body.data;
    if (Array.isArray(body?.results)) return body.results;
    return [];
  }

  /**
   * โครงร่างการโหลดที่ทุกแท็บใช้ร่วมกัน ต่างกันแค่ประเภทใบเสร็จและวิธีแปลงข้อมูล
   */
  private loadReceiptLogTab(
    tabId: string,
    typeReceipt: string,
    label: string,
    mapRows: (rows: any[]) => any[],
    assign: (records: any[]) => void
  ): void {
    this.fetchAllReceiptLogs(typeReceipt)
      .then(rows => {
        this.ngZone.run(() => {
          const records = mapRows(rows);
          assign(records);
          this.tabCounts[tabId] = records.length;
          this.applyFilters();
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      })
      .catch((error: HttpErrorResponse) => {
        this.ngZone.run(() => {
          console.error(`❌ โหลดข้อมูล${label}ไม่สำเร็จ:`, error);
          this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล${label}: ${error.message}`;
          assign([]);
          this.tabCounts[tabId] = 0;
          this.applyFilters();
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      });
  }

  /**
   * Load renew member records from receipt-log API (typeReceipt=formRenewMember)
   */
  loadRenewMemberRecords(): void {
    this.loadReceiptLogTab(
      'renew-member', 'formRenewMember', 'ใบคำขอต่ออายุสมาชิก',
      rows => this.mapFormReceiptLogs(rows, 'formRenewMember'),
      records => { this.renewMemberRecords = records; }
    );
  }

  /**
   * Load regis appraiser records from receipt-log API (typeReceipt=formRegisAppraiser)
   */
  loadRegisAppraiserRecords(): void {
    this.loadReceiptLogTab(
      'register-appraiser', 'formRegisAppraiser', 'ใบคำขอขึ้นทะเบียนผู้ประเมิน',
      rows => this.mapFormReceiptLogs(rows, 'formRegisAppraiser'),
      records => { this.regisAppraiserRecords = records; }
    );
  }

  /**
   * Load renew appraiser records from receipt-log API (typeReceipt=formRenewAppraiser)
   */
  loadRenewAppraiserRecords(): void {
    this.loadReceiptLogTab(
      'renew-appraiser', 'formRenewAppraiser', 'ใบคำขอต่ออายุบัตรผู้ประเมิน',
      rows => this.mapFormReceiptLogs(rows, 'formRenewAppraiser'),
      records => { this.renewAppraiserRecords = records; }
    );
  }

  /**
   * Load training records from receipt-log API (typeReceipt=cpdHours)
   */
  loadTrainingRecords(): void {
    this.loadReceiptLogTab(
      'training', 'cpdHours', 'การอบรม',
      rows => this.mapCpdReceiptLogs(rows),
      records => { this.trainingRecords = records; }
    );
  }

  /**
   * Load exam records from receipt-log API (typeReceipt=examList)
   */
  loadExamRecords(): void {
    this.loadReceiptLogTab(
      'exam', 'examList', 'การสมัครสอบ',
      rows => rows,
      records => { this.examRecords = records; }
    );
  }

  /**
   * Load member card records from receipt-log API (typeReceipt=formRequestNewMember)
   */
  loadMemberCardRecords(): void {
    this.loadReceiptLogTab(
      'member-card', 'formRequestNewMember', 'ใบคำขอทำบัตรสมาชิก',
      rows => this.mapFormReceiptLogs(rows, 'formRequestNewMember'),
      records => { this.memberCardRecords = records; }
    );
  }

  /**
   * Update counts for all tabs
   */
  updateTabCounts(): void {
    console.log('🔢 Updating tab counts...');
    
    // member-card and renew-member counts are updated separately in their respective load methods
    this.tabCounts['register-appraiser'] = this.records.filter(r => this.getReceiptType(r) === 'register-appraiser').length;
    this.tabCounts['renew-appraiser'] = this.records.filter(r => this.getReceiptType(r) === 'renew-appraiser').length;
    this.tabCounts['training'] = this.records.filter(r => r.courseId !== null && r.courseId !== undefined).length;
    this.tabCounts['exam'] = this.records.filter(r => r.examId !== null && r.examId !== undefined).length;
    
    console.log('📊 Tab counts:', this.tabCounts);
  }

  /**
   * Get receipt type based on payment record
   * This is a placeholder - you'll need to add actual logic based on your data structure
   */
  getReceiptType(record: PaymentRecord): string {
    // TODO: Implement actual logic to determine receipt type
    // For now, return based on courseId/examId
    if (record.courseId) return 'training';
    if (record.examId) return 'exam';
    // You may need to add additional fields to PaymentRecord to distinguish between
    // member-card, renew-member, register-appraiser, renew-appraiser
    return 'member-card'; // default
  }

  /**
   * Switch between tabs and reload data
   */
  switchTab(tabId: 'member-card' | 'renew-member' | 'register-appraiser' | 'renew-appraiser' | 'training' | 'exam'): void {
    console.log('🔄 Switching to tab:', tabId);
    this.activeTab = tabId;
    this.page = 1; // Reset to first page
    
    // Reload data for the selected tab
    this.ngZone.run(() => {
      if (tabId === 'member-card') {
        this.loadMemberCardRecords();
      } else if (tabId === 'renew-member') {
        this.loadRenewMemberRecords();
      } else if (tabId === 'register-appraiser') {
        this.loadRegisAppraiserRecords();
      } else if (tabId === 'renew-appraiser') {
        this.loadRenewAppraiserRecords();
      } else if (tabId === 'training') {
        this.loadTrainingRecords();
      } else if (tabId === 'exam') {
        this.loadExamRecords();
      } else {
        this.applyTabFilter();
      }
    });
  }

  /**
   * Apply tab filter to records
   */
  applyTabFilter(): void {
    console.log('🔍 Applying tab filter:', this.activeTab);
    
    if (this.activeTab === 'training') {
      this.filteredRecords = this.records.filter(r => r.courseId !== null && r.courseId !== undefined);
    } else if (this.activeTab === 'exam') {
      this.filteredRecords = this.records.filter(r => r.examId !== null && r.examId !== undefined);
    } else {
      // Filter by receipt type for other tabs
      this.filteredRecords = this.records.filter(r => this.getReceiptType(r) === this.activeTab);
    }
    
    this.collectionSize = this.filteredRecords.length;
    this.cdr.detectChanges();
  }

  /**
   * Get count for specific tab
   */
  getTabCount(tabId: string): number {
    return this.tabCounts[tabId] || 0;
  }

  /**
   * Handle page change
   */
  onPageChange(newPage: number): void {
    console.log('📄 Page changed to:', newPage);
    this.page = newPage;
    this.cdr.detectChanges();
  }

  /**
   * Apply filters to record list (client-side filtering)
   */
  applyFilters(): void {
    try {
      const source = this.getActiveSourceRecords();
      const filtered = (source || []).filter((record) => this.recordMatchesFilters(record));
      this.setActiveFilteredRecords(filtered);
      this.collectionSize = filtered.length;
      this.page = 1; // Reset to first page when filtering
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Error in applyFilters:', error);
      const source = this.getActiveSourceRecords();
      this.setActiveFilteredRecords([...(source || [])]);
      this.collectionSize = (source || []).length;
      this.cdr.detectChanges();
    }
  }

  /** The unfiltered source array for the currently active tab. */
  private getActiveSourceRecords(): any[] {
    switch (this.activeTab) {
      case 'member-card': return this.memberCardRecords;
      case 'renew-member': return this.renewMemberRecords;
      case 'register-appraiser': return this.regisAppraiserRecords;
      case 'renew-appraiser': return this.renewAppraiserRecords;
      case 'training': return this.trainingRecords;
      case 'exam': return this.examRecords;
      default: return this.records;
    }
  }

  /** Write the filtered result back to the active tab's filtered array. */
  private setActiveFilteredRecords(arr: any[]): void {
    switch (this.activeTab) {
      case 'member-card': this.filteredMemberCardRecords = arr as any; break;
      case 'renew-member': this.filteredRenewMemberRecords = arr as any; break;
      case 'register-appraiser': this.filteredRegisAppraiserRecords = arr as any; break;
      case 'renew-appraiser': this.filteredRenewAppraiserRecords = arr as any; break;
      case 'training': this.filteredTrainingRecords = arr as any; break;
      case 'exam': this.filteredExamRecords = arr as any; break;
      default: this.filteredRecords = arr as any; break;
    }
  }

  /** Tab-aware field extractors so one filter works across all record shapes. */
  private getRecordEmail(record: any): string {
    return (this.activeTab === 'exam' ? record?.examList?.user?.email : record?.user?.email) || '';
  }

  private getRecordCid(record: any): string {
    return (this.activeTab === 'exam' ? record?.examList?.user?.cid : record?.user?.cid) || '';
  }

  private getRecordName(record: any): string {
    const ui = this.activeTab === 'exam'
      ? record?.examList?.user?.userInformation
      : record?.user?.userInformation;
    const first = ui?.firstName || ui?.firstNameThai || ui?.firstNameEnglish || '';
    const last = ui?.lastName || ui?.lastNameThai || ui?.lastNameEnglish || '';
    return `${first} ${last}`.trim();
  }

  private getRecordTitle(record: any): string {
    switch (this.activeTab) {
      case 'member-card':
      case 'renew-member': return record?.membershipType || '';
      case 'register-appraiser':
      case 'renew-appraiser': return record?.appraiserLevel || '';
      case 'training': return record?.course?.title || '';
      case 'exam': return record?.examList?.sessionTitle || '';
      default: return record?.course?.title || record?.exam?.title || '';
    }
  }

  private getRecordCode(record: any): string {
    return record?.codeReceipt || '';
  }

  private getRecordReceiptType(record: any): string {
    return record?.type || ''; // 'created' | 'canceled'
  }

  private getRecordAmount(record: any): number {
    return parseFloat(record?.amount ?? record?.feeAmount ?? '0') || 0;
  }

  /** Parseable ISO date for the record (receipt-log createdDate). */
  private getRecordDate(record: any): string {
    return record?.createdDate || record?.createdAt || '';
  }

  private recordMatchesFilters(record: any): boolean {
    const f = this.filters;

    // General search across email, cid, name, receipt code, item title
    if (f.searchTerm) {
      const s = f.searchTerm.toLowerCase();
      const haystack = [
        this.getRecordEmail(record),
        this.getRecordCid(record),
        this.getRecordName(record),
        this.getRecordCode(record),
        this.getRecordTitle(record),
      ].join(' ').toLowerCase();
      if (!haystack.includes(s)) return false;
    }

    if (f.email && !this.getRecordEmail(record).toLowerCase().includes(f.email.toLowerCase())) {
      return false;
    }

    if (f.codeReceipt && !this.getRecordCode(record).toLowerCase().includes(f.codeReceipt.toLowerCase())) {
      return false;
    }

    if (f.itemTitle && !this.getRecordTitle(record).toLowerCase().includes(f.itemTitle.toLowerCase())) {
      return false;
    }

    if (f.receiptType && this.getRecordReceiptType(record) !== f.receiptType) {
      return false;
    }

    const amount = this.getRecordAmount(record);
    if (f.amountMin && amount < parseFloat(f.amountMin)) return false;
    if (f.amountMax && amount > parseFloat(f.amountMax)) return false;

    if (f.dateFrom || f.dateTo) {
      const recordDate = new Date(this.getRecordDate(record));
      if (isNaN(recordDate.getTime())) return false; // no parseable date → exclude when filtering by date
      if (f.dateFrom) {
        const from = new Date(f.dateFrom);
        from.setHours(0, 0, 0, 0);
        if (recordDate < from) return false;
      }
      if (f.dateTo) {
        const to = new Date(f.dateTo);
        to.setHours(23, 59, 59, 999);
        if (recordDate > to) return false;
      }
    }

    return true;
  }

  /**
   * Get current page records
   */
  getCurrentPageRecords(): any[] {
    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    
    // Return member card records if on member-card tab
    if (this.activeTab === 'member-card') {
      return this.filteredMemberCardRecords.slice(startIndex, endIndex);
    }
    
    // Return renew member records if on renew-member tab
    if (this.activeTab === 'renew-member') {
      return this.filteredRenewMemberRecords.slice(startIndex, endIndex);
    }

    // Return regis appraiser records if on register-appraiser tab
    if (this.activeTab === 'register-appraiser') {
      return this.filteredRegisAppraiserRecords.slice(startIndex, endIndex);
    }

    // Return renew appraiser records if on renew-appraiser tab
    if (this.activeTab === 'renew-appraiser') {
      return this.filteredRenewAppraiserRecords.slice(startIndex, endIndex);
    }

    // Return training records if on training tab
    if (this.activeTab === 'training') {
      return this.filteredTrainingRecords.slice(startIndex, endIndex);
    }
    
    // Return exam records if on exam tab
    if (this.activeTab === 'exam') {
      return this.filteredExamRecords.slice(startIndex, endIndex);
    }
    
    return this.filteredRecords.slice(startIndex, endIndex);
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      email: '',
      codeReceipt: '',
      itemTitle: '',
      receiptType: '',
      amountMin: '',
      amountMax: '',
      dateFrom: '',
      dateTo: ''
    };
    this.page = 1;
    this.applyFilters();
  }

  /**
   * Sort records by field
   */
  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    // เรียงลำดับเฉพาะรายการของแท็บที่เปิดอยู่ (แต่ละแท็บเก็บข้อมูลคนละชุด)
    this.getCurrentFilteredRecords().sort((a, b) => {
      const aValue = this.getSortValue(a, field);
      const bValue = this.getSortValue(b, field);

      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    this.page = 1;
    this.cdr.detectChanges();
  }

  /**
   * ค่าที่ใช้เปรียบเทียบตอนเรียงลำดับของแต่ละคอลัมน์
   */
  private getSortValue(record: any, field: string): any {
    switch (field) {
      case 'submittedAt': {
        const raw = this.getRowSubmittedAtRaw(record);
        const time = raw ? new Date(raw).getTime() : NaN;
        return isNaN(time) ? 0 : time;
      }
      case 'name': return this.getRowFullName(record);
      case 'memberNo': return this.getRowMemberNo(record);
      case 'organization': return this.getRowOrganization(record);
      case 'itemTitle': return this.getRowItemTitle(record);
      case 'amount': return this.getRowAmount(record);
      case 'paymentStatus': return this.getRowPaymentStatus(record);
      case 'paidAt': return this.getRowPaidAt(record);
      case 'paymentSlip': return this.getRowPaymentSlip(record) ? 1 : 0;
      default: return '';
    }
  }

  /**
   * คอลัมน์ที่กำลังใช้เรียงลำดับอยู่ (ใช้ไฮไลต์หัวตาราง)
   */
  isSortedBy(field: string): boolean {
    return this.sortField === field;
  }

  /**
   * Get sort icon for column
   */
  getSortIcon(field: string): string {
    if (this.sortField !== field) return '';
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  /**
   * Change page size
   */
  changePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
    this.cdr.detectChanges();
  }

  /**
   * Refresh data
   */
  refreshData(): void {
    console.log('🔄 Refreshing data...');
    this.page = 1;
    this.loadPaymentRecords();
  }

  /**
   * Export to CSV
   */
  /**
   * รายการทั้งหมดของแท็บปัจจุบันที่ผ่านตัวกรองแล้ว (ไม่ตัดตามหน้า)
   */
  private getCurrentFilteredRecords(): any[] {
    switch (this.activeTab) {
      case 'member-card': return this.filteredMemberCardRecords || [];
      case 'renew-member': return this.filteredRenewMemberRecords || [];
      case 'register-appraiser': return this.filteredRegisAppraiserRecords || [];
      case 'renew-appraiser': return this.filteredRenewAppraiserRecords || [];
      case 'training': return this.filteredTrainingRecords || [];
      case 'exam': return this.filteredExamRecords || [];
      default: return this.filteredRecords || [];
    }
  }

  /**
   * จำนวนรายการของแท็บปัจจุบันหลังกรอง (ใช้เปิด/ปิดปุ่ม Export)
   */
  get currentFilteredCount(): number {
    return this.getCurrentFilteredRecords().length;
  }

  /**
   * ชื่อรายการตามแท็บ (ใช้ตั้งชื่อไฟล์ CSV)
   */
  private getActiveTabLabel(): string {
    return this.tabs.find(tab => tab.id === this.activeTab)?.label || 'รายการ';
  }

  exportToCSV(): void {
    if (this.isExporting) {
      return;
    }

    console.log('📊 Exporting to CSV...');

    // ทุกแท็บโหลดข้อมูลมาทั้งหมดแล้ว (กรอง/แบ่งหน้าฝั่ง client)
    const rows = this.getCurrentFilteredRecords();

    if (rows.length === 0) {
      alert('ไม่พบข้อมูลที่ตรงกับเงื่อนไขการกรอง');
      return;
    }

    this.isExporting = true;
    this.cdr.detectChanges();

    // เรียงคอลัมน์ให้ตรงกับตาราง แล้วตามด้วยข้อมูลเสริมที่ไม่ได้แสดงบนหน้าจอ
    const headers = [
      'วันและเวลาที่ทำรายการ',
      'ชื่อ-นามสกุล',
      'เลขที่สมาชิกสมาคม',
      'บริษัท/หน่วยงาน',
      'ชื่อรายการ',
      'จำนวนเงิน (บาท)',
      'สถานะการชำระเงิน',
      'วันที่และเวลาชำระเงิน',
      'หลักฐานการชำระเงิน',
      'อีเมล',
      'เลขบัตรประชาชน',
      'เบอร์โทร',
      'VAT Expired',
      'Valuer ID',
      'Valuer Expired',
      'เลขที่ใบเสร็จ',
      'ประเภทใบเสร็จ',
      'หมายเหตุการยกเลิก'
    ];

    const csvRows = rows.map(record => [
      this.getRowSubmittedAt(record),
      this.getRowFullName(record),
      this.getRowMemberNo(record),
      this.getRowOrganization(record),
      this.getRowItemTitle(record),
      this.getRowAmount(record).toFixed(2),
      this.getRowPaymentStatus(record),
      this.getRowPaidAt(record),
      this.getRowPaymentSlip(record) || '-',
      this.getCsvEmail(record),
      this.getCsvCid(record) || '-',
      this.getRowPhoneNumber(record),
      this.getRowVatExpiry(record),
      this.getRowValuerNo(record),
      this.getRowValuerExpiry(record),
      record?.codeReceipt || '-',
      this.getReceiptLogTypeLabel(record?.type) || '-',
      record?.cancelReason || '-'
    ]);

    downloadCsv(`account-worklist_${this.activeTab}_${csvFileDateSuffix()}`, headers, csvRows);
    console.log(`✅ Exported ${csvRows.length} rows (${this.getActiveTabLabel()}) to CSV`);

    this.isExporting = false;
    this.cdr.detectChanges();
  }

  // ── ตัวช่วยอ่านค่าแต่ละคอลัมน์สำหรับ CSV (โครงสร้างข้อมูลต่างกันในแต่ละแท็บ) ──

  private getCsvEmail(record: any): string {
    return (this.activeTab === 'exam' ? record?.examList?.user?.email : record?.user?.email) || '-';
  }

  private getCsvCid(record: any): string {
    return (this.activeTab === 'exam' ? record?.examList?.user?.cid : record?.user?.cid) || '';
  }

  private getCsvName(record: any): string {
    const info = this.activeTab === 'exam'
      ? record?.examList?.user?.userInformation
      : record?.user?.userInformation;
    const first = info?.firstName || info?.firstNameThai || info?.firstNameEnglish || record?.user?.firstName || '';
    const last = info?.lastName || info?.lastNameThai || info?.lastNameEnglish || record?.user?.lastName || '';
    return `${first} ${last}`.trim();
  }

  private getCsvTitle(record: any): string {
    switch (this.activeTab) {
      case 'member-card':
      case 'renew-member': return record?.membershipType || '';
      case 'register-appraiser':
      case 'renew-appraiser': return record?.appraiserLevel || '';
      case 'training': return record?.course?.title || '';
      case 'exam': return record?.examList?.sessionTitle || '';
      default: return record?.course?.title || record?.exam?.title || '';
    }
  }

  private getCsvAmount(record: any): number {
    switch (this.activeTab) {
      case 'member-card':
      case 'renew-member': return parseFloat(record?.feeAmount || '0') || 0;
      case 'training': return parseFloat(record?.course?.price || '0') || 0;
      default: return parseFloat(record?.amount || '0') || 0;
    }
  }

  private getCsvPaymentStatus(record: any): string {
    switch (this.activeTab) {
      case 'training': return record?.cpdPaymentStatus?.name || '-';
      case 'exam': return record?.status || '-';
      case 'member-card':
      case 'renew-member':
      case 'register-appraiser':
      case 'renew-appraiser': return record?.paymentStatus || '-';
      default: return record?.paymentStatus?.name || '-';
    }
  }

  // ── ตัวช่วยอ่านค่าแต่ละคอลัมน์ของตาราง (โครงสร้างข้อมูลต่างกันในแต่ละแท็บ) ──

  /** ข้อมูลผู้ใช้ของแถว (แท็บสมัครสอบเก็บผู้ใช้ไว้ใต้ examList) */
  private getRowUser(record: any): any {
    return (this.activeTab === 'exam' ? record?.examList?.user : record?.user) || null;
  }

  private getRowUserInformation(record: any): any {
    return this.getRowUser(record)?.userInformation || null;
  }

  /** วันและเวลาที่ User เข้าทำรายการในระบบ (ค่าดิบ ใช้สำหรับเรียงลำดับ) */
  private getRowSubmittedAtRaw(record: any): string | null {
    if (this.activeTab === 'exam') {
      return record?.examList?.createdAt || record?.examList?.enrolledAt || record?.createdDate || null;
    }
    return record?.createdAt || record?.createdDate || null;
  }

  /** 1. วันและเวลาที่ User เข้าทำรายการในระบบ */
  getRowSubmittedAt(record: any): string {
    return this.formatDateTime(this.getRowSubmittedAtRaw(record));
  }

  /** 2. ชื่อ-นามสกุล User */
  getRowFullName(record: any): string {
    const info = this.getRowUserInformation(record);
    const user = this.getRowUser(record);
    const first = info?.firstName || info?.firstNameThai || info?.firstNameEnglish || user?.firstName || '';
    const last = info?.lastName || info?.lastNameThai || info?.lastNameEnglish || user?.lastName || '';
    return `${first} ${last}`.trim() || '-';
  }

  /** 3. เลขที่สมาชิกสมาคม */
  getRowMemberNo(record: any): string {
    return this.getRowUserInformation(record)?.vatId || '-';
  }

  /** 4. บริษัท/หน่วยงาน */
  getRowOrganization(record: any): string {
    const info = this.getRowUserInformation(record);
    return info?.company?.organizationName || info?.workplaceName || info?.organization || '-';
  }

  /** 5. ชื่อรายการ */
  getRowItemTitle(record: any): string {
    return this.getCsvTitle(record) || '-';
  }

  /** 6. จำนวนเงิน */
  getRowAmount(record: any): number {
    return this.getCsvAmount(record);
  }

  /** 7. สถานะการชำระเงิน */
  getRowPaymentStatus(record: any): string {
    return this.getCsvPaymentStatus(record);
  }

  getRowPaymentStatusClass(record: any): string {
    switch (this.activeTab) {
      case 'member-card':
      case 'renew-member':
      case 'register-appraiser':
      case 'renew-appraiser':
      case 'training':
      case 'exam':
        return this.getMemberCardPaymentStatusClass(this.getRowPaymentStatus(record));
      default:
        return this.getPaymentStatusBadgeClass(record);
    }
  }

  /** 8. วันที่และเวลาชำระเงิน (API ส่ง transferDateTime มาเป็นข้อความไทยอยู่แล้ว) */
  getRowPaidAt(record: any): string {
    return record?.transferDateTime || this.formatDateTime(record?.paymentDate) || '-';
  }

  /** 9. หลักฐานการชำระเงิน */
  getRowPaymentSlip(record: any): string | null {
    const slip = this.activeTab === 'exam' ? record?.examList?.paymentSlip : record?.paymentSlip;
    return slip && String(slip).trim() !== '' ? String(slip) : null;
  }

  // ── ข้อมูลเพิ่มเติมสำหรับไฟล์ CSV (ไม่ได้แสดงบนตาราง) ──

  /** เบอร์โทรติดต่อของผู้ทำรายการ */
  getRowPhoneNumber(record: any): string {
    return getPhoneNumberFrom(this.getRowUserInformation(record));
  }

  /** เลขที่ผู้ประเมินหลัก */
  getRowValuerNo(record: any): string {
    return this.getRowUserInformation(record)?.valuerId || '-';
  }

  /** วันหมดอายุบัตรสมาชิก VAT (ตลอดชีพแสดงคำว่า "ตลอดชีพ") */
  getRowVatExpiry(record: any): string {
    const info = this.getRowUserInformation(record);
    return formatMemberExpiry(info?.vatType, info?.vatExpired, date => this.formatDate(date));
  }

  /** วันหมดอายุบัตรผู้ประเมินหลัก */
  getRowValuerExpiry(record: any): string {
    const info = this.getRowUserInformation(record);
    return formatMemberExpiry(info?.valuerType, info?.valuerExpired, date => this.formatDate(date));
  }

  /**
   * Format date + time for display
   */
  formatDateTime(dateString?: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    return `${date.toLocaleDateString('th-TH')} ${date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.`;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH');
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount: number): string {
    return amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * Parse float helper for template
   */
  parseFloat(value: string): number {
    return parseFloat(value) || 0;
  }

  /**
   * Get payment status badge class
   */
  getPaymentStatusBadgeClass(record: PaymentRecord): string {
    const statusName = record.paymentStatus.name;
    
    switch (statusName) {
      case 'ชำระเงินเสร็จสิ้น':
      case 'ชำระแล้ว':
      case 'อนุมัติแล้ว':
        return 'bg-success';
      case 'รอชำระเงิน':
      case 'ยังไม่ได้ชำระเงิน':
        return 'bg-warning';
      case 'รอตรวจสอบยอดชำระ':
      case 'รอตรวจสอบ':
      case 'รอการอนุมัติ':
        return 'bg-info';
      case 'ยกเลิก':
      case 'ไม่อนุมัติ':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get member card payment status badge class
   */
  getMemberCardPaymentStatusClass(status: string): string {
    switch (status) {
      case 'ชำระเงินเสร็จสิ้น':
        return 'bg-success';
      case 'รอชำระเงิน':
      case 'ยังไม่ได้ชำระเงิน':
        return 'bg-warning';
      case 'รอตรวจสอบ':
      case 'รอการอนุมัติ':
        return 'bg-info';
      case 'ยกเลิก':
      case 'ไม่อนุมัติ':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get item type badge class
   */
  getItemTypeBadgeClass(record: PaymentRecord): string {
    if (record.courseId) return 'bg-primary';
    if (record.examId) return 'bg-info';
    return 'bg-secondary';
  }

  /**
   * Get item type text
   */
  getItemTypeText(record: PaymentRecord): string {
    if (record.courseId) return 'คอร์สอบรม';
    if (record.examId) return 'การสอบ';
    return 'อื่นๆ';
  }

  /**
   * Get item title
   */
  getItemTitle(record: PaymentRecord): string {
    if (record.course) return record.course.title;
    if (record.exam) return record.exam.title;
    return '-';
  }

  /**
   * View payment slip
   */
  viewPaymentSlip(record: any): void {
    const slip = this.getRowPaymentSlip(record);
    if (!slip) {
      alert('ไม่มีหลักฐานการชำระเงิน');
      return;
    }

    this.openSlipPreview(slip);
  }

  // ── หน้าต่างดูหลักฐานการชำระเงิน (ย่อ-ขยายได้) ──

  /**
   * เปิดหลักฐานการโอนในหน้าต่างซ้อน พร้อมปุ่มย่อ-ขยาย
   */
  openSlipPreview(url: string): void {
    this.slipPreviewUrl = url;
    this.slipPreviewIsPdf = /\.pdf(\?|#|$)/i.test(url);
    this.resetSlipZoom();
    this.showSlipPreview = true;
    this.cdr.detectChanges();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showSlipPreview) {
      this.closeSlipPreview();
    }
  }

  closeSlipPreview(): void {
    this.showSlipPreview = false;
    this.slipPreviewUrl = null;
    this.slipDragging = false;
    this.resetSlipZoom();
    this.cdr.detectChanges();
  }

  zoomSlipIn(): void {
    this.setSlipZoom(this.slipZoom + this.SLIP_ZOOM_STEP);
  }

  zoomSlipOut(): void {
    this.setSlipZoom(this.slipZoom - this.SLIP_ZOOM_STEP);
  }

  resetSlipZoom(): void {
    this.slipZoom = 1;
    this.slipOffsetX = 0;
    this.slipOffsetY = 0;
    this.cdr.detectChanges();
  }

  private setSlipZoom(value: number): void {
    const next = Math.min(this.SLIP_ZOOM_MAX, Math.max(this.SLIP_ZOOM_MIN, Math.round(value * 100) / 100));
    if (next === this.slipZoom) return;
    this.slipZoom = next;
    if (next <= 1) {
      // ย่อกลับมาเท่าเดิมแล้วให้ภาพกลับไปอยู่กึ่งกลาง
      this.slipOffsetX = 0;
      this.slipOffsetY = 0;
    }
    this.cdr.detectChanges();
  }

  get slipZoomPercent(): number {
    return Math.round(this.slipZoom * 100);
  }

  /** ย่อ-ขยายด้วยลูกกลิ้งเมาส์ */
  onSlipWheel(event: WheelEvent): void {
    event.preventDefault();
    this.setSlipZoom(this.slipZoom + (event.deltaY < 0 ? this.SLIP_ZOOM_STEP : -this.SLIP_ZOOM_STEP));
  }

  /** ลากเลื่อนภาพเมื่อขยายจนล้นกรอบ */
  onSlipDragStart(event: MouseEvent): void {
    if (this.slipZoom <= 1) return;
    event.preventDefault();
    this.slipDragging = true;
    this.slipDragStartX = event.clientX - this.slipOffsetX;
    this.slipDragStartY = event.clientY - this.slipOffsetY;
  }

  onSlipDragMove(event: MouseEvent): void {
    if (!this.slipDragging) return;
    event.preventDefault();
    this.slipOffsetX = event.clientX - this.slipDragStartX;
    this.slipOffsetY = event.clientY - this.slipDragStartY;
    this.cdr.detectChanges();
  }

  onSlipDragEnd(): void {
    this.slipDragging = false;
  }

  /** เปิดไฟล์หลักฐานในแท็บใหม่ (เผื่อผู้ใช้ต้องการบันทึก/พิมพ์) */
  openSlipInNewTab(): void {
    if (this.slipPreviewUrl) {
      window.open(this.slipPreviewUrl, '_blank');
    }
  }

  /**
   * Generate receipt/tax invoice
   */
  generateReceipt(record: PaymentRecord): void {
    console.log('📄 Generate receipt for:', record);
    // Navigate to renew-slip component with payment ID and tab type
    this.router.navigate(['/staff/renew-slip', record.id], {
      queryParams: { type: this.activeTab, receiptLogId: record.receiptLogId, returnUrl: this.router.url }
    });
  }

  /**
   * Preview receipt with sample data
   */
  previewReceipt(): void {
    console.log('👁️ Preview receipt with sample data');
    // Navigate to renew-slip component without ID to show sample data
    this.router.navigate(['/staff/renew-slip']);
  }

  /**
   * View record details
   */
  viewRecord(record: any): void {
    console.log('👁️ View record:', record);
    if (this.activeTab === 'exam') {
      this.openExamReceiptDialog(record);
      return;
    }
    if (['member-card', 'renew-member', 'register-appraiser', 'appraiser-renewal', 'renew-appraiser'].includes(this.activeTab)) {
      this.openFormReceiptDialog(record, this.activeTab);
      return;
    }
    this.router.navigate(['/staff/renew-slip', record.id], {
      queryParams: { type: this.activeTab, receiptLogId: record.receiptLogId, returnUrl: this.router.url }
    });
  }

  private getFormReceiptLogApiUrl(formEntityId: string, tabType: string): string {
    const base = environment.apiUrl;
    switch (tabType) {
      case 'member-card': return `${base}/receipt-log/form-request-new-member/${formEntityId}`;
      case 'renew-member': return `${base}/receipt-log/form-renew-member/${formEntityId}`;
      case 'register-appraiser': return `${base}/receipt-log/form-regis-appraiser/${formEntityId}`;
      case 'appraiser-renewal':
      case 'renew-appraiser': return `${base}/receipt-log/form-renew-appraiser/${formEntityId}`;
      default: return '';
    }
  }

  openFormReceiptDialog(record: any, tabType: string): void {
    this.selectedFormEntityId = record.id;
    this.selectedFormTabType = tabType;
    this.receiptLogLoading = true;
    this.receiptLogs = [];
    this.receiptLogSummary = null;

    this.modalService.open(this.receiptLogModal, {
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });

    const apiUrl = this.getFormReceiptLogApiUrl(record.id, tabType);
    this.http.get<ReceiptLogListResponse>(apiUrl).subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          this.receiptLogs = response.data || [];
          this.receiptLogSummary = response.summary || null;
          this.receiptLogLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('❌ Failed to load form receipt logs:', error);
          this.receiptLogLoading = false;
          this.cdr.detectChanges();
        });
      }
    });
  }

  /**
   * Fetch receipt-log versions for an exam record and open a selection dialog
   */
  openExamReceiptDialog(record: ReceiptLog): void {
    const apiUrl = `${environment.apiUrl}/receipt-log/exam-list/${record.examListId}`;
    this.receiptLogLoading = true;
    this.receiptLogs = [];
    this.receiptLogSummary = null;

    this.modalService.open(this.receiptLogModal, {
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });

    this.http.get<ReceiptLogListResponse>(apiUrl).subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          this.receiptLogs = response.data || [];
          this.receiptLogSummary = response.summary || null;
          this.receiptLogLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('❌ Failed to load receipt logs:', error);
          this.receiptLogLoading = false;
          this.cdr.detectChanges();
        });
      }
    });
  }

  /**
   * Navigate to the receipt page for the chosen receipt-log entry
   */
  selectReceiptLog(receiptLog: ReceiptLog): void {
    this.modalService.dismissAll();
    if (this.selectedFormEntityId && this.selectedFormTabType) {
      // Form type: use form entity ID as the primary route param so cancel/reissue
      // operations in renew-slip can PATCH the correct form entity.
      // Pass receiptLogId so renew-slip loads the authoritative codeReceipt from this log.
      this.router.navigate(['/staff/renew-slip', this.selectedFormEntityId], {
        queryParams: { type: this.selectedFormTabType, receiptLogId: receiptLog.id, returnUrl: this.router.url }
      });
      this.selectedFormEntityId = null;
      this.selectedFormTabType = null;
    } else {
      // Exam type: receipt log IS the primary record
      this.router.navigate(['/staff/renew-slip', receiptLog.id], {
        queryParams: { type: 'exam', returnUrl: this.router.url }
      });
    }
  }

  getReceiptLogTypeBadgeClass(type: string): string {
    switch (type) {
      case 'created': return 'bg-success';
      case 'canceled': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  getReceiptLogTypeLabel(type: string): string {
    switch (type) {
      case 'created': return 'สร้างใหม่';
      case 'canceled': return 'ยกเลิก';
      default: return type;
    }
  }

  /**
   * Approve payment
   */
  approvePayment(record: PaymentRecord): void {
    console.log('✅ Approve payment:', record);
    const confirmation = confirm(`คุณต้องการอนุมัติการชำระเงินจำนวน ${this.formatCurrency(record.amount)} บาท หรือไม่?`);
    
    if (confirmation) {
      const apiUrl = `${environment.apiUrl}/payments/${record.id}/approve`;
      console.log('📡 Sending POST request to:', apiUrl);

      this.http.post(apiUrl, {}).subscribe({
        next: (response) => {
          console.log('✅ API Response:', response);
          this.loadPaymentRecords();
          alert(`✅ อนุมัติการชำระเงินเรียบร้อยแล้ว`);
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ API Error:', error);
          alert(`เกิดข้อผิดพลาดในการอนุมัติ: ${error.message}`);
        }
      });
    }
  }

  /**
   * Reject payment
   */
  rejectPayment(record: PaymentRecord): void {
    console.log('❌ Reject payment:', record);
    const reason = prompt('กรุณาระบุเหตุผลในการไม่อนุมัติ:');
    
    if (reason) {
      const apiUrl = `${environment.apiUrl}/payments/${record.id}/reject`;
      console.log('📡 Sending POST request to:', apiUrl);

      this.http.post(apiUrl, { reason }).subscribe({
        next: (response) => {
          console.log('✅ API Response:', response);
          this.loadPaymentRecords();
          alert(`❌ ไม่อนุมัติการชำระเงินเรียบร้อยแล้ว`);
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ API Error:', error);
          alert(`เกิดข้อผิดพลาดในการไม่อนุมัติ: ${error.message}`);
        }
      });
    }
  }

  /**
   * Delete record
   */
  deleteRecord(record: PaymentRecord): void {
    console.log('🗑️ Delete record:', record);
    const confirmation = confirm(`คุณต้องการลบรายการชำระเงิน ${this.formatCurrency(record.amount)} บาท หรือไม่?\n\nการดำเนินการนี้ไม่สามารถยกเลิกได้!`);
    
    if (confirmation) {
      const apiUrl = `${environment.apiUrl}/payments/${record.id}`;
      console.log('📡 Sending DELETE request to:', apiUrl);

      this.http.delete(apiUrl).subscribe({
        next: (response) => {
          console.log('✅ API Response:', response);
          const index = this.records.findIndex(r => r.id === record.id);
          if (index > -1) {
            this.records.splice(index, 1);
            this.applyFilters();
          }
          alert(`✅ ลบรายการชำระเงินเรียบร้อยแล้ว`);
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ API Error:', error);
          alert(`เกิดข้อผิดพลาดในการลบ: ${error.message}`);
        }
      });
    }
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByRecordId(index: number, record: PaymentRecord): string {
    return record.id;
  }

  /**
   * Math helper for template
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  /**
   * Handle dropdown open/close state
   */
  onDropdownOpenChange(isOpen: boolean, recordId: string): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const dropdownButton = document.getElementById(`dropdownMenuButton-${recordId}`);
        const tableRow = dropdownButton?.closest('tr');
        const tableContainer = dropdownButton?.closest('.table-responsive');

        if (tableRow) {
          if (isOpen) {
            tableRow.classList.add('dropdown-active');
            tableRow.style.position = 'relative';
            tableRow.style.zIndex = '1050';

            if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            }

            const rowRect = tableRow.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;

            if (dropdownMenu && rowRect.bottom > viewportHeight - 200) {
              dropdownMenu.style.top = 'auto';
              dropdownMenu.style.bottom = '100%';
              dropdownMenu.style.marginBottom = '2px';
              dropdownMenu.style.marginTop = '0';
            }
          } else {
            tableRow.classList.remove('dropdown-active');
            tableRow.style.position = '';
            tableRow.style.zIndex = '';

            if (tableContainer && window.innerWidth >= 992) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            } else if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = '';
            }

            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;
            if (dropdownMenu) {
              dropdownMenu.style.top = '';
              dropdownMenu.style.bottom = '';
              dropdownMenu.style.marginBottom = '';
              dropdownMenu.style.marginTop = '';
            }
          }
        }
      }, 0);
    });
  }
}
